import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download, Smartphone } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  
  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }
    
    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (new Date() - dismissedDate) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }
    
    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);
    
    if (isIOSDevice) {
      // Show iOS instructions after a delay
      setTimeout(() => setShowPrompt(true), 3000);
    } else {
      // Listen for beforeinstallprompt event (Android/Desktop)
      const handleBeforeInstallPrompt = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowPrompt(true);
      };
      
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      
      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }
    
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('BKA PWA: User accepted install');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };
  
  const handleDismiss = () => {
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
    setShowPrompt(false);
    setShowIOSInstructions(false);
  };
  
  if (!showPrompt) return null;
  
  return (
    <>
      {/* Install Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-zinc-950 border-t border-zinc-800 safe-area-inset-bottom">
        <div className="max-w-md mx-auto flex items-center space-x-3">
          <div className="flex-shrink-0 w-12 h-12 bg-black rounded-lg flex items-center justify-center border border-zinc-800">
            <span className="text-white font-black text-lg">BKA</span>
          </div>
          
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium text-sm">Instalar BKA</p>
            <p className="text-zinc-400 text-xs truncate">
              {isIOS ? 'Agregar a pantalla de inicio' : 'Acceso rápido desde tu dispositivo'}
            </p>
          </div>
          
          <Button
            onClick={handleInstallClick}
            size="sm"
            className="bg-primary hover:bg-[#b20710] text-white font-bold"
          >
            <Download className="h-4 w-4 mr-1" />
            Instalar
          </Button>
          
          <button
            onClick={handleDismiss}
            className="p-2 text-zinc-500 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/80" onClick={handleDismiss}>
          <div 
            className="w-full max-w-md bg-zinc-950 rounded-t-2xl p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-heading text-white uppercase">Instalar BKA en iPhone</h3>
              <button onClick={handleDismiss} className="text-zinc-500">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold">1</span>
                </div>
                <div>
                  <p className="text-white text-sm">Toca el botón de compartir</p>
                  <p className="text-zinc-500 text-xs mt-1">
                    Es el ícono con la flecha hacia arriba en la barra de Safari
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold">2</span>
                </div>
                <div>
                  <p className="text-white text-sm">Selecciona "Agregar a pantalla de inicio"</p>
                  <p className="text-zinc-500 text-xs mt-1">
                    Desliza hacia abajo en el menú para encontrar la opción
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold">3</span>
                </div>
                <div>
                  <p className="text-white text-sm">Toca "Agregar"</p>
                  <p className="text-zinc-500 text-xs mt-1">
                    ¡Listo! BKA aparecerá en tu pantalla de inicio
                  </p>
                </div>
              </div>
            </div>
            
            <Button
              onClick={handleDismiss}
              className="w-full bg-zinc-800 hover:bg-zinc-700 text-white"
            >
              Entendido
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
